import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  CreateManualPaymentMethodBody,
  CreateOrderHolidayHoursBody,
  CreateOrderShiftBody,
  DeliveryZoneBody,
  ManualPaymentMethod,
  OrderCapacityMode,
  OrderHolidayHours,
  OrderShift,
  OrderTypeConfig,
  OrderTypeConfigBody,
  PauseOrderBody,
  PaymentSuccess,
  UpdateDeliveryZoneBody,
  UpdateManualPaymentMethodBody,
  UpdateOrderHolidayHoursBody,
  UpdateOrderShiftBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

const CAPACITY_MODE_PATH = '/api/restaurants/{restaurantId}/order-capacity-mode';
const SHIFTS_PATH = '/api/restaurants/{restaurantId}/order-shifts';
const SHIFT_PATH = '/api/restaurants/{restaurantId}/order-shifts/{shiftId}';
const HOLIDAYS_PATH = '/api/restaurants/{restaurantId}/order-holiday-hours';
const HOLIDAY_PATH = '/api/restaurants/{restaurantId}/order-holiday-hours/{holidayId}';

// ─── Query keys ──────────────────────────────────────────────────────────────

const setupKeys = {
  capacityMode: (restaurantId: string, orderType: string) =>
    ['order-setup', restaurantId, 'capacity-mode', orderType] as const,
  shifts: (restaurantId: string, orderType: string, activeOnly: boolean) =>
    ['order-setup', restaurantId, 'shifts', orderType, activeOnly] as const,
  holidays: (restaurantId: string, orderType: string) =>
    ['order-setup', restaurantId, 'holidays', orderType] as const,
  config: (restaurantId: string) => ['order-setup', restaurantId, 'config'] as const,
  pauseState: (restaurantId: string) => ['order-setup', restaurantId, 'pause-state'] as const,
  channelStatus: (restaurantId: string) => ['order-setup', restaurantId, 'channel-status'] as const,
  zones: (restaurantId: string) => ['order-setup', restaurantId, 'zones'] as const,
  deliveryReadiness: (restaurantId: string) =>
    ['order-setup', restaurantId, 'delivery-readiness'] as const,
  methods: (restaurantId: string) => ['order-setup', restaurantId, 'methods'] as const,
};

// ─── Order capacity mode (SIMPLE | ADVANCED, per channel) ─────────────────────

export function useOrderCapacityMode(orderType: 'PICKUP' | 'DELIVERY') {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.capacityMode(restaurantId, orderType),
    queryFn: async () =>
      unwrapResult<OrderCapacityMode>(
        await openapiClient.GET(CAPACITY_MODE_PATH, {
          params: { path: { restaurantId }, query: { orderType } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function useSetOrderCapacityMode() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderCapacityMode) =>
      unwrapResult<OrderCapacityMode>(
        await openapiClient.PUT(CAPACITY_MODE_PATH, { params: { path: { restaurantId } }, body }),
      ),
    // Switching the mode changes which shifts are active and what's "set up".
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId] });
    },
  });
}

// ─── OrderShifts ─────────────────────────────────────────────────────────────

export function useOrderShifts(orderType: 'PICKUP' | 'DELIVERY', activeOnly?: boolean) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.shifts(restaurantId, orderType, activeOnly ?? false),
    queryFn: async () =>
      unwrapResult<OrderShift[]>(
        await openapiClient.GET(SHIFTS_PATH, {
          params: { path: { restaurantId }, query: { orderType, activeOnly } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

interface ShiftLeadTimes {
  prepTimeMinutes?: number | null;
  schedulingLeadMinutes?: number | null;
}

function trackShiftTiming(data: ShiftLeadTimes): void {
  if (data.prepTimeMinutes === undefined && data.schedulingLeadMinutes === undefined) return;
  trackEvent('delivery_timing_changed', {
    prepMinutes: data.prepTimeMinutes ?? 'inherit',
    noticeMinutes: data.schedulingLeadMinutes ?? 'inherit',
  });
}

export function useCreateOrderShift() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOrderShiftBody) =>
      unwrapResult<OrderShift>(
        await openapiClient.POST(SHIFTS_PATH, { params: { path: { restaurantId } }, body: data }),
      ),
    onSuccess: (_res, data) => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'shifts'] });
      trackShiftTiming(data);
    },
  });
}

export function useUpdateOrderShift() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ shiftId, data }: { shiftId: string; data: UpdateOrderShiftBody }) =>
      unwrapResult<OrderShift>(
        await openapiClient.PATCH(SHIFT_PATH, {
          params: { path: { restaurantId, shiftId } },
          body: data,
        }),
      ),
    onSuccess: (_res, { data }) => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'shifts'] });
      trackShiftTiming(data);
    },
  });
}

export function useDeleteOrderShift() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shiftId: string) => {
      unwrapResult(
        await openapiClient.DELETE(SHIFT_PATH, { params: { path: { restaurantId, shiftId } } }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'shifts'] });
    },
  });
}

// ─── OrderHolidayHours ────────────────────────────────────────────────────────

export function useOrderHolidayHours(orderType: 'PICKUP' | 'DELIVERY') {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.holidays(restaurantId, orderType),
    queryFn: async () =>
      unwrapResult<OrderHolidayHours[]>(
        await openapiClient.GET(HOLIDAYS_PATH, {
          params: { path: { restaurantId }, query: { orderType } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function useCreateOrderHolidayHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOrderHolidayHoursBody) =>
      unwrapResult<OrderHolidayHours>(
        await openapiClient.POST(HOLIDAYS_PATH, {
          params: { path: { restaurantId } },
          body: data,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'holidays'] });
    },
  });
}

export function useUpdateOrderHolidayHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      holidayId,
      data,
    }: {
      holidayId: string;
      data: UpdateOrderHolidayHoursBody;
    }) =>
      unwrapResult<OrderHolidayHours>(
        await openapiClient.PATCH(HOLIDAY_PATH, {
          params: { path: { restaurantId, holidayId } },
          body: data,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'holidays'] });
    },
  });
}

export function useDeleteOrderHolidayHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (holidayId: string) => {
      unwrapResult(
        await openapiClient.DELETE(HOLIDAY_PATH, {
          params: { path: { restaurantId, holidayId } },
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId, 'holidays'] });
    },
  });
}

// ─── OrderTypeConfig ──────────────────────────────────────────────────────────

export function useOrderTypeConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.config(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/order-config', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useUpdateOrderTypeConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  const key = setupKeys.config(restaurantId);
  return useMutation({
    mutationFn: async ({
      orderType,
      data,
    }: {
      orderType: 'PICKUP' | 'DELIVERY';
      data: OrderTypeConfigBody;
    }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/order-config/{orderType}', {
          params: { path: { restaurantId, orderType } },
          body: data,
        }),
      );
    },
    // Optimistically patch the cached row so a toggle flips INSTANTLY and sticks,
    // instead of snapping back to the server value until the refetch lands (an
    // F7 controlled-toggle bound to `checked` reverts on the next render otherwise
    // — the "toggle won't slide" bug). Roll back the snapshot if the write fails.
    onMutate: async ({ orderType, data }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OrderTypeConfig[]>(key);
      queryClient.setQueryData<OrderTypeConfig[]>(key, (old) =>
        old?.map((c) => (c.orderType === orderType ? { ...c, ...data } : c)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
    },
    // Reconcile with the server truth (derived fields like onlineProviderConfigured)
    // whether the write succeeded or failed.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      // A delivery price-mode / distance-calculation change retires the zones the
      // new shape no longer consults, in the same transaction as the config write
      // (UpsertOrderTypeConfigUseCase). Re-read both or the setup screen keeps
      // rendering zones the server just deactivated, and the warning banner
      // disagrees with the list under it.
      queryClient.invalidateQueries({ queryKey: setupKeys.zones(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
    },
  });
}

export function useOrderPauseState() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.pauseState(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/order-pause', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
    // Timed pauses expire on their own - re-poll so the badge flips back to
    // "Accepting" without the operator having to refresh.
    refetchInterval: 60_000,
  });
}

/**
 * Effective per-channel status for the orders workspace strip: OPEN / CLOSED /
 * PAUSED plus the next change time. Folds opening hours + holiday overrides +
 * pause into one verdict, so "Live" can't lie when the channel is closed by
 * hours. Polls so a scheduled open/close flips on its own.
 */
export function useOrderChannelStatus() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.channelStatus(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/order-channel-status', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: 60_000,
  });
}

export function usePauseOrders() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PauseOrderBody) => {
      return unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/orders/pause', {
          params: { path: { restaurantId } },
          body: input,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.config(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.pauseState(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.channelStatus(restaurantId) });
    },
  });
}

export function useUnpauseOrders() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderType: 'PICKUP' | 'DELIVERY' }) => {
      return unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/orders/unpause', {
          params: { path: { restaurantId } },
          body: input,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.config(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.pauseState(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.channelStatus(restaurantId) });
    },
  });
}

// ─── Delivery Zones ───────────────────────────────────────────────────────────

export function useDeliveryZones() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.zones(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/delivery-zones', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

/**
 * What about the current delivery setup would break checkout for a real guest.
 * Server-computed from the same config the storefront quote path reads, so the
 * warning can never disagree with the actual failure.
 */
export function useDeliveryReadiness() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.deliveryReadiness(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/delivery-readiness', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useCreateDeliveryZone() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: DeliveryZoneBody) => {
      return unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/delivery-zones', {
          params: { path: { restaurantId } },
          body: data,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.zones(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
    },
  });
}

export function useUpdateDeliveryZone() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, data }: { zoneId: string; data: UpdateDeliveryZoneBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/delivery-zones/{zoneId}', {
          params: { path: { restaurantId, zoneId } },
          body: data,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.zones(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
    },
  });
}

export function useDeleteDeliveryZone() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (zoneId: string) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/restaurants/{restaurantId}/delivery-zones/{zoneId}', {
          params: { path: { restaurantId, zoneId } },
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.zones(restaurantId) });
      queryClient.invalidateQueries({ queryKey: setupKeys.deliveryReadiness(restaurantId) });
    },
  });
}

// ─── Manual Payment Methods ───────────────────────────────────────────────────

export function useManualPaymentMethods() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: setupKeys.methods(restaurantId),
    queryFn: async () => {
      return unwrapResult<ManualPaymentMethod[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/manual-payment-methods', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useCreateManualPaymentMethod() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateManualPaymentMethodBody) =>
      unwrapResult<ManualPaymentMethod>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/manual-payment-methods', {
          params: { path: { restaurantId } },
          body: data,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.methods(restaurantId) });
    },
  });
}

export function useUpdateManualPaymentMethod() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      methodId,
      data,
    }: {
      methodId: string;
      data: UpdateManualPaymentMethodBody;
    }) =>
      unwrapResult<ManualPaymentMethod>(
        await openapiClient.PATCH(
          '/api/restaurants/{restaurantId}/manual-payment-methods/{methodId}',
          { params: { path: { restaurantId, methodId } }, body: data },
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.methods(restaurantId) });
    },
  });
}

export function useDeleteManualPaymentMethod() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (methodId: string) =>
      unwrapResult<PaymentSuccess>(
        await openapiClient.DELETE(
          '/api/restaurants/{restaurantId}/manual-payment-methods/{methodId}',
          { params: { path: { restaurantId, methodId } } },
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.methods(restaurantId) });
    },
  });
}
